import React, { useState, useEffect } from "react";
import { activityAPI } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Users,
  Search,
  Clock,
  LogIn,
  Eye,
  Edit,
  Plus,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const actionIcons = {
  login: LogIn,
  view: Eye,
  update: Edit,
  create: Plus,
  export: Download,
};

export const UsersPage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await activityAPI.getAll();
      setActivities(response.items);
    } catch (err) {
      toast.error("Failed to fetch user activity");
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = activities.filter((activity) => {
    return (
      activity.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.user_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.action.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const getActionColor = (action) => {
    switch (action) {
      case "login":
        return "bg-green-100 text-green-800";
      case "view":
        return "bg-blue-100 text-blue-800";
      case "update":
        return "bg-amber-100 text-amber-800";
      case "create":
        return "bg-purple-100 text-purple-800";
      case "export":
        return "bg-slate-100 text-slate-800";
      default:
        return "bg-slate-100 text-slate-800";
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  const getInitials = (name) => {
    return (
      name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase() || "U"
    );
  };

  // Get unique users from activities
  const uniqueUsers = [
    ...new Map(activities.map((a) => [a.user_email, a])).values(),
  ];

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64"
        data-testid="users-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Page Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
          Users & Activity
        </h1>
        <p className="text-muted-foreground mt-1">
          Monitor user access and activity in your organization
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card
          className="border-border shadow-sm"
          data-testid="total-users-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Users
                </p>
                <p className="font-heading text-3xl font-bold">
                  {uniqueUsers.length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-primary/10">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className="border-border shadow-sm"
          data-testid="active-sessions-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Active Sessions
                </p>
                <p className="font-heading text-3xl font-bold">
                  {activities.filter((a) => a.action === "login").length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-green-100">
                <LogIn className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className="border-border shadow-sm"
          data-testid="total-actions-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Actions
                </p>
                <p className="font-heading text-3xl font-bold">
                  {activities.length}
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-100">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">
            Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {uniqueUsers.map((user, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                data-testid={`user-card-${index}`}
              >
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {getInitials(user.user_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{user.user_name}</p>
                  <p className="text-sm text-muted-foreground truncate">
                    {user.user_email}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="border-border shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search activity by user, email, or action..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="activity-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Activity Table */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-lg font-semibold">
            Recent Activity ({filteredActivities.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActivities.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No activity found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredActivities.map((activity) => {
                    const ActionIcon = actionIcons[activity.action] || Eye;
                    return (
                      <TableRow
                        key={activity.id}
                        data-testid={`activity-row-${activity.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                {getInitials(activity.user_name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm">
                                {activity.user_name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {activity.user_email}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            className={`${getActionColor(activity.action)} capitalize`}
                          >
                            <ActionIcon className="w-3 h-3 mr-1" />
                            {activity.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{activity.resource}</span>
                        </TableCell>
                        <TableCell>
                          <code className="ip-address">
                            {activity.ip_address}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(activity.timestamp)}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
